import { NavigateFunction, useNavigate } from 'react-router';
import { useState } from 'react';
import { useBankIDAuthInitMutation, useClientQuery } from 'hooks/webapi';
import useRedirect from 'hooks/webapi/useRedirect';
import { BANK_ID_ERROR, MANUAL_OCR } from 'Router/routes';
import {
  BankIdErrorProps,
  BankIdErrorPropsMap,
  getErrorValueByKey,
} from '../BankIdError/errorTranslation';

// Persists the error code to sessionStorage so the BankError page can read it after navigation
export const handleErrorRedirect = (
  navigate: NavigateFunction,
  reason?: BankIdErrorProps | string,
  skipFormat = false,
) => {
  // Persist the error status so BankError page can read it after navigation
  sessionStorage.setItem(
    'sessionStatus',
    skipFormat
      ? reason ?? ''
      : getErrorValueByKey(
          (Number(reason) || 1) as keyof typeof BankIdErrorPropsMap,
        ),
  );

  navigate(BANK_ID_ERROR);
};

export default function useBankIdInfo() {
  const navigate = useNavigate();
  const redirect = useRedirect();

  const {
    mutateAsync: initBankIdSession,
    isPending: initBankIdSessionIsPending,
  } = useBankIDAuthInitMutation();
  const { refetch: refetchClient } = useClientQuery();

  const [isLoadingOverride, setIsLoadingOverride] = useState(false);

  const initBankId = async () => {
    setIsLoadingOverride(true);

    try {
      const { data: client } = await refetchClient();

      // If client is not loaded, we can't proceed
      if (!client) {
        handleErrorRedirect(navigate, 'client_not_loaded');
        setIsLoadingOverride(false);
        return;
      }

      const redirectURL = await initBankIdSession({
        product: import.meta.env.VITE_PSD2_PRODUCT_NAME,
        clientNumber: client?.number || '',
      });

      // If redirectURL is not loaded, we can't proceed
      if (!redirectURL) {
        handleErrorRedirect(navigate, 'redirect_url_not_loaded');
        return;
      }

      const sessionId = new URLSearchParams(redirectURL.url).get('state');

      // If sessionId is not loaded, we can't proceed
      if (!sessionId) {
        handleErrorRedirect(navigate, 'session_id_not_loaded');
        return;
      }

      localStorage.setItem('bankIdSessionId', sessionId);
      redirect(redirectURL.url);
    } catch (e) {
      // TODO: Handle error
    } finally {
      // As the actual redirection takes some time, we need to override the loading state for a while to prevent double initiation
      setTimeout(() => {
        setIsLoadingOverride(false);
      }, 10 * 1000);
    }
  };

  const handleSkip = () => {
    navigate(MANUAL_OCR);
  };

  return {
    initBankId,
    handleSkip,
    isPending: initBankIdSessionIsPending || isLoadingOverride,
  };
}
